import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Select,
  MenuItem,
  Button,
  CircularProgress,
  Chip,
  Tooltip,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material';
import {
  Refresh as RefreshIcon,
  NavigateBefore as PrevIcon,
  NavigateNext as NextIcon,
  CheckCircle as AccountedIcon,
  Cancel as NotAccountedIcon,
  RadioButtonUnchecked as NotCheckedIcon,
  PersonAdd as AddedIcon,
  Flag as CheckpointIcon,
  PlayArrow as CreatedIcon,
  Stop as ClosedIcon,
  Info as DefaultIcon,
} from '@mui/icons-material';
import { IncidentActivityItem, IncidentActivityPage } from '../../Types/Incident';
import { getIncidentActivityPage } from '../../Services/IncidentService';
import { formatIncidentActivity } from '../../Utils/activityFormatter';
import { appColors } from '../../Config/Theme';

interface RecentActivityPanelProps {
  incidentId: string;
  /** Bump this value to trigger a refresh from outside (e.g. realtime event). */
  refreshTrigger?: number;
}

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];
const DEFAULT_PAGE_SIZE = 25;

const activityIcon = (item: IncidentActivityItem) => {
  const t = (item.activityType ?? '').toLowerCase();
  const s = (item.effectiveStatus ?? '').toLowerCase();
  if (t.includes('checkpoint')) return <CheckpointIcon sx={{ fontSize: 18, color: appColors.primary.main }} />;
  if (t.includes('created')) return <CreatedIcon sx={{ fontSize: 18, color: appColors.primary.main }} />;
  if (t.includes('closed')) return <ClosedIcon sx={{ fontSize: 18, color: appColors.text.caption }} />;
  if (t.includes('manualadd') || t.includes('personadded') || t.includes('unlistedpersonadded'))
    return <AddedIcon sx={{ fontSize: 18, color: appColors.primary.main }} />;
  if (s === 'accounted') return <AccountedIcon sx={{ fontSize: 18, color: appColors.status.success }} />;
  if (s === 'notaccounted') return <NotAccountedIcon sx={{ fontSize: 18, color: appColors.status.error }} />;
  if (s === 'notchecked') return <NotCheckedIcon sx={{ fontSize: 18, color: appColors.text.caption }} />;
  return <DefaultIcon sx={{ fontSize: 18, color: appColors.text.caption }} />;
};

const chipColor = (tone?: string): { bg: string; text: string } => {
  switch (tone) {
    case 'success': return { bg: appColors.status.successBg, text: appColors.status.success };
    case 'attention': return { bg: '#FFEBEE', text: appColors.status.error };
    case 'neutral': return { bg: appColors.surface.inactive, text: appColors.text.body };
    default: return { bg: appColors.surface.inactive, text: appColors.text.body };
  }
};

const AUTO_REFRESH_MS = 15_000;

const RecentActivityPanel: React.FC<RecentActivityPanelProps> = ({ incidentId, refreshTrigger }) => {
  const [page, setPage] = useState<IncidentActivityPage | null>(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastRefreshed, setLastRefreshed] = useState<string | null>(null);

  const fetchPage = useCallback(async (pn: number, ps: number, showSpinner: boolean) => {
    if (showSpinner) setLoading(true);
    setError(null);
    try {
      const result = await getIncidentActivityPage(incidentId, pn, ps);
      setPage(result);
      setLastRefreshed(new Date().toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', second: '2-digit' }));
    } catch {
      setError('Unable to load activity.');
    } finally {
      setLoading(false);
    }
  }, [incidentId]);

  useEffect(() => {
    fetchPage(pageNumber, pageSize, true);
  }, [pageNumber, pageSize, fetchPage]);

  // Refresh when external trigger changes (realtime events)
  useEffect(() => {
    if (refreshTrigger != null && refreshTrigger > 0 && pageNumber === 1) {
      fetchPage(1, pageSize, false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshTrigger]);

  // Auto-refresh every 15 seconds (silent, no spinner)
  useEffect(() => {
    const timer = setInterval(() => {
      fetchPage(pageNumber, pageSize, false);
    }, AUTO_REFRESH_MS);
    return () => clearInterval(timer);
  }, [pageNumber, pageSize, fetchPage]);

  const handleRefresh = () => fetchPage(pageNumber, pageSize, false);
  const handlePrev = () => setPageNumber((p) => Math.max(1, p - 1));
  const handleNext = () => setPageNumber((p) => p + 1);
  const handlePageSizeChange = (e: SelectChangeEvent<number>) => {
    setPageSize(Number(e.target.value));
    setPageNumber(1);
  };

  // Compute range text
  const rangeStart = page ? (pageNumber - 1) * pageSize + 1 : 0;
  const rangeEnd = page ? rangeStart + page.items.length - 1 : 0;
  const rangeText = page?.totalCount != null
    ? `Showing ${rangeStart}–${rangeEnd} of ${page.totalCount} activity records`
    : page && page.items.length > 0
      ? `Showing ${page.items.length} activity records`
      : '';

  return (
    <Box
      sx={{
        bgcolor: appColors.surface.card,
        borderRadius: '12px',
        border: `1px solid ${appColors.border.default}`,
        p: 2.5,
        width: '100%',
      }}
    >
      {/* ── Header ── */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: appColors.text.heading }}>
            Recent Activity
          </Typography>
          <Typography variant="caption" sx={{ color: appColors.text.placeholder }}>
            Newest activity across this incident
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {lastRefreshed && (
            <Typography variant="caption" sx={{ color: appColors.text.placeholder, whiteSpace: 'nowrap' }}>
              Updated {lastRefreshed}
            </Typography>
          )}
          <Tooltip title="Refresh activity">
            <IconButton
              size="small"
              onClick={handleRefresh}
              aria-label="Refresh activity"
              disabled={loading}
            >
              <RefreshIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* ── Loading state ── */}
      {loading && !page && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={24} />
          <Typography variant="body2" sx={{ ml: 1, color: appColors.text.placeholder }}>
            Loading activity…
          </Typography>
        </Box>
      )}

      {/* ── Error state ── */}
      {error && (
        <Box sx={{ textAlign: 'center', py: 3 }}>
          <Typography variant="body2" sx={{ color: appColors.status.error, mb: 1 }}>
            {error}
          </Typography>
          <Button size="small" variant="outlined" onClick={handleRefresh}>
            Retry
          </Button>
        </Box>
      )}

      {/* ── Empty state ── */}
      {!loading && !error && page && page.items.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="body2" sx={{ color: appColors.text.disabled, mb: 0.5 }}>
            No activity has been recorded yet.
          </Typography>
          <Typography variant="caption" sx={{ color: appColors.text.placeholder }}>
            Headcount updates and checkpoints will appear here.
          </Typography>
        </Box>
      )}

      {/* ── Activity rows ── */}
      {page && page.items.length > 0 && (
        <Box sx={{ mt: 1.5 }}>
          {page.items.map((item, idx) => {
            const fmt = formatIncidentActivity(item);
            const chip = fmt.chipLabel ? chipColor(fmt.chipTone) : null;
            return (
              <Box
                key={item.activityId ?? idx}
                sx={{
                  display: 'flex',
                  gap: 1.5,
                  py: 1.25,
                  borderTop: idx > 0 ? `1px solid ${appColors.border.light}` : 'none',
                  alignItems: 'flex-start',
                }}
              >
                {/* Time */}
                <Typography
                  variant="caption"
                  sx={{ color: appColors.text.placeholder, minWidth: 58, pt: 0.25, flexShrink: 0 }}
                >
                  {fmt.timeText}
                </Typography>

                {/* Icon */}
                <Box sx={{ pt: 0.25, flexShrink: 0 }}>{activityIcon(item)}</Box>

                {/* Text */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" sx={{ color: appColors.text.heading, lineHeight: 1.4 }}>
                    {fmt.primaryText}
                  </Typography>
                  <Typography variant="caption" sx={{ color: appColors.text.placeholder }}>
                    {fmt.secondaryText}
                  </Typography>
                </Box>

                {/* Chip */}
                {chip && fmt.chipLabel && (
                  <Chip
                    label={fmt.chipLabel}
                    size="small"
                    sx={{
                      bgcolor: chip.bg,
                      color: chip.text,
                      fontWeight: 600,
                      fontSize: '0.7rem',
                      height: 22,
                      flexShrink: 0,
                    }}
                  />
                )}
              </Box>
            );
          })}
        </Box>
      )}

      {/* ── Pagination footer ── */}
      {page && page.items.length > 0 && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mt: 2,
            pt: 1.5,
            borderTop: `1px solid ${appColors.border.default}`,
            flexWrap: 'wrap',
            gap: 1,
          }}
        >
          {/* Range text */}
          <Typography variant="caption" sx={{ color: appColors.text.placeholder }}>
            {rangeText}
            {page.totalCount == null && ` · Page ${pageNumber}`}
          </Typography>

          {/* Controls */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="caption" sx={{ color: appColors.text.caption, mr: 0.5 }}>
              Rows per page:
            </Typography>
            <Select
              value={pageSize}
              onChange={handlePageSizeChange}
              size="small"
              variant="outlined"
              aria-label="Rows per page"
              sx={{ fontSize: '0.75rem', height: 28, minWidth: 56 }}
            >
              {PAGE_SIZE_OPTIONS.map((opt) => (
                <MenuItem key={opt} value={opt} sx={{ fontSize: '0.75rem' }}>
                  {opt}
                </MenuItem>
              ))}
            </Select>

            <IconButton
              size="small"
              onClick={handlePrev}
              disabled={!page.hasPreviousPage}
              aria-label="Previous page"
            >
              <PrevIcon fontSize="small" />
            </IconButton>

            {page.totalPages != null ? (
              <Typography variant="caption" sx={{ color: appColors.text.caption, mx: 0.5 }}>
                {pageNumber} of {page.totalPages}
              </Typography>
            ) : (
              <Typography variant="caption" sx={{ color: appColors.text.caption, mx: 0.5 }}>
                Page {pageNumber}
              </Typography>
            )}

            <IconButton
              size="small"
              onClick={handleNext}
              disabled={!page.hasNextPage}
              aria-label="Next page"
            >
              <NextIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default React.memo(RecentActivityPanel);
